import { useIntl } from "react-intl";
import * as Yup from "yup";
import { forwardRef, useImperativeHandle, useState } from "react";
import axios from "axios";
import { getIn, useFormik } from "formik";
import clsx from "clsx";
import InputMask from "react-input-mask";
import { fieldsInterface } from "./FormModel";
import { useStyleContext } from "../../helpers/StyleContext";
import { toastfy } from "../toast/Toast";
import { showErrorMessage } from "../../helpers/MessagesHelper";
import { getAuth } from "../../../app/modules/auth";
import { InputText } from "./components/fields/InputText";

interface PropsAddressModal {
  _user_id: number;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

export interface FormHandlesAddressModal {
  handleSubmit: () => void;
  fetchData: () => void;
}

// 🟢 TIPO ATUALIZADO: Aceitando disabled explícito e colClass
type CustomFieldInterface = fieldsInterface & { colClass?: string; disabled?: boolean };

const UsersAddressModal: React.ForwardRefRenderFunction<
  FormHandlesAddressModal,
  PropsAddressModal
> = ({ _user_id, setIsLoading }, ref) => {
  const intl = useIntl();
  const module = "users";
  const operationUpdate = "users/address/update"; 
  const [isLoadingForm, setIsLoadingForm] = useState(true);

  const { styleState } = useStyleContext();

  const fields: CustomFieldInterface[] = [
    {
      description: `${intl.formatMessage({
        id: "MODULE.FIELDSINTERFACE.ZIPCODE",
      })}`,
      reference: "postalCode",
      field: {
        element: "input",
        placeholder: `${intl.formatMessage({
          id: "MODULE.FIELDSINTERFACE.ZIPCODE.PLACEHOLDER",
        })}`,
      },
      required: true,
      disabled: true,
      colClass: "col-md-2",
    },
    {
      description: `${intl.formatMessage({
        id: "MODULE.FIELDSINTERFACE.ADRESS",
      })}`,
      reference: "address",
      field: {
        element: "input",
        type: "text",
        placeholder: `${intl.formatMessage({
          id: "MODULE.FIELDSINTERFACE.ADRESS.PLACEHOLDER",
        })}`,
      },
      required: true,
      disabled: true,
      colClass: "col-md-8",
    },
    {
      description: `${intl.formatMessage({
        id: "MODULE.FIELDSINTERFACE.NUMBER",
      })}`,
      reference: "number",
      field: {
        element: "input",
        type: "text",
        placeholder: `${intl.formatMessage({
          id: "MODULE.FIELDSINTERFACE.NUMBER.PLACEHOLDER",
        })}`,
      },
      required: true,
      disabled: true,
      colClass: "col-md-2",
    },
    {
      description: `${intl.formatMessage({
        id: "MODULE.FIELDSINTERFACE.NEIGHBORHOOD",
      })}`,
      reference: "neighborhood",
      field: {
        element: "input",
        type: "text",
        placeholder: `${intl.formatMessage({
          id: "MODULE.FIELDSINTERFACE.NEIGHBORHOOD.PLACEHOLDER",
        })}`,
      },
      required: true,
      disabled: true,
      colClass: "col-md-4",
    },
    {
      description: `${intl.formatMessage({
        id: "MODULE.FIELDSINTERFACE.CITY",
      })}`,
      reference: "city",
      field: {
        element: "input",
        type: "text",
        placeholder: `${intl.formatMessage({
          id: "MODULE.FIELDSINTERFACE.CITY.PLACEHOLDER",
        })}`,
      },
      required: true,
      disabled: true,
      colClass: "col-md-4",
    },
    {
      description: `${intl.formatMessage({
        id: "MODULE.FIELDSINTERFACE.STATE",
      })}`,
      reference: "state",
      field: {
        element: "input",
        type: "text",
        placeholder: `${intl.formatMessage({
          id: "MODULE.FIELDSINTERFACE.STATE.PLACEHOLDER",
        })}`,
      },
      required: true,
      disabled: true,
      colClass: "col-md-2",
    },
    {
      description: `${intl.formatMessage({
        id: "MODULE.FIELDSINTERFACE.COUNTRY",
      })}`,
      reference: "country",
      field: {
        element: "input",
        type: "text",
        placeholder: `${intl.formatMessage({
          id: "MODULE.FIELDSINTERFACE.COUNTRY.PLACEHOLDER",
        })}`,
      },
      required: true,
      disabled: true,
      colClass: "col-md-2",
    },
  ];

  const validationSchema = Yup.object().shape({
    postalCode: Yup.string().test(
      "valid-cep",
      `${intl.formatMessage({
        id: "MODULE.VALIDATIONSCHEMA.REQUIRED_POSTALCODE_INVALID",
      })}`,
      (value) => value === "" || /^\d{5}-\d{3}$/.test(value ?? "")
    ),
    address: Yup.string().required(
      `${intl.formatMessage({
        id: "MODULE.VALIDATIONSCHEMA.REQUIRED_ADDRESS",
      })}`
    ),
    number: Yup.string().required(
      `${intl.formatMessage({
        id: "MODULE.VALIDATIONSCHEMA.REQUIRED_NUMBER",
      })}`
    ),
    neighborhood: Yup.string().required(
      `${intl.formatMessage({
        id: "MODULE.VALIDATIONSCHEMA.REQUIRED_NEIGHBORHOOD",
      })}`
    ),
    city: Yup.string().required(
      `${intl.formatMessage({
        id: "MODULE.VALIDATIONSCHEMA.REQUIRED_CITY",
      })}`
    ),
    state: Yup.string().required(
      `${intl.formatMessage({
        id: "MODULE.VALIDATIONSCHEMA.REQUIRED_STATE",
      })}`
    ),
    country: Yup.string().required(
      `${intl.formatMessage({
        id: "MODULE.VALIDATIONSCHEMA.REQUIRED_COUNTRY",
      })}`
    ),
  });

  const initialValues: { [key: string]: string } = fields.reduce(
    (acc, field) => ({ ...acc, [field.reference]: "" }),
    {}
  );

  const apiToken = getAuth();
  const token = apiToken?.api_token;
  const apiUrl = process.env.REACT_APP_API_URL;

  const handleSubmit = (values: any, { setSubmitting }: any) => {
    setIsLoading(true);

    const headers = {
      Authorization: `Bearer ${token}`,
      operation: operationUpdate,
    };

    axios
      .put(`${apiUrl}/${module}/address/edit/${_user_id}`, values, {
        headers,
      })
      .then((response) => {
        setIsLoading(false);
        toastfy("success", "Endereço atualizado com sucesso!");
      })
      .catch((error) => {
        showErrorMessage(error?.response?.status, error?.response?.data?.error);
        const timeoutId = setTimeout(() => {
          setIsLoading(false);
        }, 1000);
        return () => clearTimeout(timeoutId);
      });
    setSubmitting(false);
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: handleSubmit,
  });

  const fetchData = async () => {
    const headers = {
      Authorization: `Bearer ${token}`,
      operation: `${module}/address/find-by-user-id`,
    };

    try {
      await axios
        .get(`${apiUrl}/${module}/address/${_user_id}`, {
          headers,
        })
        .then((response) => {
          const data = response.data;
          setIsLoadingForm(false);

          const addressData = data.address || data; 

          Object.keys(addressData).forEach((key) => {
            if (addressData[key] && addressData[key].description) {
              addressData[key] = addressData[key].description;
            }
          });

          formik.setValues(addressData);
        })
        .catch((error) => {
          setIsLoadingForm(false);
          showErrorMessage(
            error?.response?.data?.error?.statusCode,
            error?.response?.data?.error?.message
          );
        });
    } catch (error) {
      console.error(error);
      setIsLoadingForm(false);
      toastfy("warning", "Erro ao carregar endereço do usuário!");
    }
  };

  useImperativeHandle(ref, () => ({
    handleSubmit: () => {
      formik.handleSubmit();
    },
    fetchData: () => {
      fetchData();
    },
  }));

  return (
    <div className="text-center mt-4">
      {!isLoadingForm ? (
        <form onSubmit={formik.handleSubmit}>
          <div className="row">
            {fields.map((field, index) => {
              // 🟢 LÓGICA DE BLOQUEIO E COR
              const isReadOnlyOrDisabled = field.readOnly === true || field.disabled === true;
              const disabledStyle = isReadOnlyOrDisabled ? { backgroundColor: "#ecedf3" } : {};

              return (
                <div
                  className={clsx(field.colClass || "col-md-4", "my-2 text-start")}
                  key={`${field.description}-${index}`}
                >
                  <label
                    className={
                      "form-label fs-6 fw-bold mb-1 " +
                      (field.required && !isReadOnlyOrDisabled ? "required" : "")
                    }
                    style={{
                      color: styleState?.secondary_text_color,
                    }}
                    htmlFor={field.reference}
                  >
                    {field.description}
                  </label>
                  <div className="w-100">
                    {field.field.element === "input" &&
                      field.field.type === "text" && (
                        <InputText
                          className={clsx(
                            "form-control form-control-sm",
                            {
                              "is-invalid":
                                getIn(formik?.touched, field.reference) &&
                                getIn(formik?.errors, field.reference),
                            },
                            {
                              "is-valid":
                                getIn(formik?.touched, field.reference) &&
                                !getIn(formik?.errors, field.reference),
                            }
                          )}
                          style={disabledStyle}
                          readOnly={field.readOnly}
                          disabled={field.disabled}
                          name={field.reference}
                          placeholder={field.field.placeholder}
                          value={getIn(formik?.values, field.reference) ?? ""}
                          handleChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          id={field.reference}
                        />
                      )}

                    {field.field.element === "input" &&
                      field.reference === "postalCode" && (
                        <InputMask
                          mask="99999-999"
                          className={clsx(
                            "form-control form-control-sm",
                            {
                              "is-invalid":
                                getIn(formik?.touched, field.reference) &&
                                getIn(formik?.errors, field.reference),
                            },
                            {
                              "is-valid":
                                getIn(formik?.touched, field.reference) &&
                                !getIn(formik?.errors, field.reference),
                            }
                          )}
                          style={disabledStyle}
                          readOnly={field.readOnly}
                          disabled={field.disabled}
                          name={field.reference}
                          id={field.reference}
                          placeholder={field.field.placeholder}
                          value={getIn(formik?.values, field.reference)}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                      )}

                    {getIn(formik.touched, field.reference) &&
                      getIn(formik.errors, field.reference) && (
                        <div className="fv-plugins-message-container mt-1">
                          <div className="fv-help-block text-danger">
                            <span role="alert">
                              {getIn(formik.errors, field.reference)}
                            </span>
                          </div>
                        </div>
                      )}
                  </div>
                </div>
              );
            })}
          </div>
        </form>
      ) : (
        <span className="spinner-border spinner-border-xl align-middle ms-2"></span>
      )}
    </div>
  );
};

export default forwardRef(UsersAddressModal);